import qrcode from 'qrcode-generator';

/**
 * Real, scannable QR matrices for the /qr-codes page (owner decision: no
 * placeholder matrices — every code on the page decodes).
 *
 * Payload: `rii.link/qr` — deliberately scheme-less. At 11 bytes it fits QR
 * version 2 (25×25 modules) at ALL four error-correction levels (v2-H caps at
 * 14 bytes; an `https://` prefix would push the H card to v3/29×29 and break
 * the page's "VERSION 2 · 25×25 MODULES" spec strip). Phone cameras treat
 * bare-domain strings as URLs.
 *
 * Rendering contract (matches the product's real generator rules):
 * - the three 7×7 finder eyes are ALWAYS standard squares — they render from
 *   `FINDER_PATH`, one shared even-odd path, constant across shape variants;
 * - module-shape styling (square/rounded/dots) applies to data modules only;
 * - the 4-module quiet zone is baked into `QR_VIEWBOX`, never cropped.
 *
 * Everything is computed once at module scope from deterministic integer
 * math — identical output on server and client, so SSR/hydration stay in
 * sync. A future swap to a dashboard-fed matrix only touches this file.
 */

export const QR_TARGET = 'rii.link/qr';

/** Three 7×7 square finder eyes as ONE even-odd path (dark ring + core). */
export const FINDER_PATH =
  'M0 0h7v7h-7zM1 1v5h5v-5zM2 2h3v3h-3zM18 0h7v7h-7zM19 1v5h5v-5zM20 2h3v3h-3zM0 18h7v7h-7zM1 19v5h5v-5zM2 20h3v3h-3z';

/** 25×25 matrix with the spec's 4-module quiet zone as viewBox margin. */
export const QR_VIEWBOX = '-2.5 -2.5 30 30';

const N = 25;

type Ec = 'L' | 'M' | 'Q' | 'H';
type Cell = readonly [row: number, col: number];

function makeCode(ec: Ec) {
  const qr = qrcode(2, ec);
  qr.addData(QR_TARGET);
  qr.make();
  return qr;
}

/** Dark data modules only — the finder zones render via FINDER_PATH. */
function dataCells(qr: ReturnType<typeof makeCode>): Cell[] {
  const cells: Cell[] = [];
  for (let r = 0; r < N; r++) {
    for (let c = 0; c < N; c++) {
      if ((r < 8 && c < 8) || (r < 8 && c >= N - 8) || (r >= N - 8 && c < 8)) continue;
      if (qr.isDark(r, c)) cells.push([r, c]);
    }
  }
  return cells;
}

// Module-shape path builders (data modules only), 1 unit = 1 module.
const sq = (cs: Cell[]) => cs.map(([r, c]) => `M${c} ${r}h1v1h-1z`).join('');
const rd = (cs: Cell[]) =>
  cs
    .map(
      ([r, c]) =>
        `M${c + 0.3} ${r}h.4q.3 0 .3 .3v.4q0 .3-.3 .3h-.4q-.3 0-.3-.3v-.4q0-.3 .3-.3z`,
    )
    .join('');
const dt = (cs: Cell[]) =>
  cs
    .map(([r, c]) => `M${c + 0.11} ${r + 0.5}a.39 .39 0 1 0 .78 0a.39 .39 0 1 0 -.78 0z`)
    .join('');

/** FNV-1a over the full module bitmap (finder included) → 6 hex chars. */
function digest(qr: ReturnType<typeof makeCode>): string {
  let h = 0x811c9dc5;
  for (let r = 0; r < N; r++) {
    for (let c = 0; c < N; c++) {
      h ^= qr.isDark(r, c) ? 1 : 0;
      h = Math.imul(h, 0x01000193);
    }
  }
  return (h >>> 0).toString(16).padStart(8, '0').slice(0, 6);
}

const codeM = makeCode('M');
const mainCells = dataCells(codeM);

/**
 * The page's checkable hash — a real digest of the rendered EC-M matrix (the
 * hero's "matrix …" readout and the anatomy spec strip both print it).
 */
export const MATRIX_DIGEST = digest(codeM);

export const QR = {
  /** EC M (the shipped default) in the three module shapes. */
  sq: sq(mainCells),
  rd: rd(mainCells),
  dt: dt(mainCells),
  /** The error-correction strip: same payload, real encodes per level. */
  L: sq(dataCells(makeCode('L'))),
  M: sq(mainCells),
  Q: sq(dataCells(makeCode('Q'))),
  H: sq(dataCells(makeCode('H'))),
} as const;
